import { z } from 'zod';
import { idSchema, isoDateTimeSchema, moneySchema, percentSchema } from './common.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { fiscalFxProofSchema } from './fiscal-fx.js';
import { vendorFiscalFxProofSchema } from './fiscal-fx-vendor.js';
import { apSelfAssessmentProofSchema } from './ap-self-assessment.js';
export const fiscalFxSourceReviewQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    q: z.string().trim().max(160).default(''),
});
export const fiscalFxSourceReviewInputSchema = z
    .object({
    invoiceId: idSchema,
    settlementId: idSchema.nullable().default(null),
    fiscalRateId: idSchema,
    reason: z.string().trim().min(20).max(2000),
})
    .strict();
const sourceComparisonFields = z.object({
    index: z.number().int().nonnegative(),
    taxRatePct: percentSchema,
    taxAccountId: idSchema.nullable(),
    postedTax: moneySchema,
    previousCorrections: moneySchema,
    fiscalTax: moneySchema,
    correction: moneySchema,
});
export const fiscalFxSourceComparisonSchema = sourceComparisonFields.refine((row) => row.taxAccountId !== null ||
    [row.taxRatePct, row.postedTax, row.previousCorrections, row.fiscalTax, row.correction].every((value) => /^-?0+(?:\.0+)?$/u.test(value)), {
    message: 'An absent tax account requires a zero-rate bucket without tax effects',
    path: ['taxAccountId'],
});
export const fiscalFxRecipientSourcePositionSchema = z.object({
    position: z.number().int().positive(),
    sourceOriginalNet: moneySchema,
    originalFiscalNet: moneySchema,
    correctedFiscalNet: moneySchema,
    originalLegalTax: moneySchema,
    correctedLegalTax: moneySchema,
    originalDeductibleTax: moneySchema,
    correctedDeductibleTax: moneySchema,
    previousOutputCorrection: moneySchema,
    previousInputCorrection: moneySchema,
    previousCostCorrection: moneySchema,
    outputCorrection: moneySchema,
    inputCorrection: moneySchema,
    costCorrection: moneySchema,
    inputTaxAccountId: idSchema,
    outputTaxAccountId: idSchema,
    costAccountId: idSchema,
    costCenterId: idSchema.nullable(),
    costObjectId: idSchema.nullable(),
});
export const fiscalFxRecipientSourceReviewSchema = z.object({
    original: apSelfAssessmentProofSchema,
    corrected: apSelfAssessmentProofSchema,
    positions: z
        .array(fiscalFxRecipientSourcePositionSchema)
        .min(1)
        .max(200)
        .refine((positions) => new Set(positions.map((position) => position.position)).size === positions.length, { message: 'Recipient source correction positions must be unique' }),
});
const sourceReviewPreviewFields = fiscalFxSourceReviewInputSchema.extend({
    sourceKind: z.enum([
        'VENDOR_INVOICE',
        'VENDOR_CANCELLATION',
        'INVOICE_ACCRUAL',
        'CUSTOMER_CASH_RECEIPT',
        'CUSTOMER_CASH_REVERSAL',
        'CUSTOMER_DISCOUNT',
    ]),
    sourceKey: z.string(),
    sourceJournalId: idSchema,
    rateTaxDate: ledgerDateSchema,
    reportingTaxDate: ledgerDateSchema,
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    supersedesId: idSchema.nullable(),
    version: z.number().int().positive(),
    proof: fiscalFxProofSchema,
    vendorFiscalFx: vendorFiscalFxProofSchema.optional(),
    recipientSelfAssessment: fiscalFxRecipientSourceReviewSchema.optional(),
    comparison: z.array(sourceComparisonFields.refine((row) => row.taxAccountId !== null ||
        [row.postedTax, row.previousCorrections, row.fiscalTax, row.correction].every((value) => /^-?0+(?:\.0+)?$/u.test(value)))),
    totalCorrection: moneySchema,
});
function validateSourceKind(value, context) {
    const supplier = value.sourceKind.startsWith('VENDOR_');
    const variants = Number(value.vendorFiscalFx !== undefined) +
        Number(value.recipientSelfAssessment !== undefined);
    if ((supplier && variants !== 1) || (!supplier && variants !== 0))
        context.addIssue({
            code: 'custom',
            path: ['vendorFiscalFx'],
            message: 'Supplier review requires exactly one typed legal and deductible source proof',
        });
    if (!value.sourceKind.startsWith('VENDOR_'))
        value.comparison.forEach((row, index) => {
            if (!fiscalFxSourceComparisonSchema.safeParse(row).success)
                context.addIssue({
                    code: 'custom',
                    path: ['comparison', index],
                    message: 'Invalid customer tax account evidence',
                });
        });
}
export const fiscalFxSourceReviewPreviewSchema = sourceReviewPreviewFields.superRefine(validateSourceKind);
export const fiscalFxSourceReviewCreateSchema = fiscalFxSourceReviewInputSchema.extend({
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    idempotencyKey: z.string().trim().min(1).max(160),
});
export const fiscalFxSourceReviewSchema = sourceReviewPreviewFields
    .extend({
    id: idSchema,
    reviewedAt: isoDateTimeSchema,
    reviewedById: idSchema,
    correctionJournalId: idSchema.nullable(),
})
    .superRefine(validateSourceKind);
export const fiscalFxSourceCandidateSchema = z.object({
    invoiceId: idSchema,
    settlementId: idSchema.nullable(),
    number: z.string(),
    currency: z.string(),
    sourceKind: z.enum([
        'VENDOR_INVOICE',
        'VENDOR_CANCELLATION',
        'INVOICE_ACCRUAL',
        'CUSTOMER_CASH_RECEIPT',
        'CUSTOMER_CASH_REVERSAL',
        'CUSTOMER_DISCOUNT',
    ]),
    rateTaxDate: ledgerDateSchema,
    reportingTaxDate: ledgerDateSchema,
    originalNet: moneySchema,
    originalTax: moneySchema,
    postedTax: moneySchema,
    latestReviewId: idSchema.nullable(),
    pinnedFiscalRateId: idSchema.nullable().default(null),
    reviewable: z.boolean(),
    blockedReason: z
        .enum([
        'ledger.errors.taxReview',
        'fiscalFx.vendorReviewEvidence',
        'fiscalFx.vendorReviewDependency',
        'fiscalFx.vendorReviewCancelled',
    ])
        .nullable(),
    supplierName: z.string().optional(),
    sourceEvidence: z.string().optional(),
    legalTax: moneySchema.optional(),
    deductibleTax: moneySchema.optional(),
});
export const fiscalFxSourceCandidatesSchema = z.object({
    data: z.array(fiscalFxSourceCandidateSchema),
    meta: z.object({
        page: z.number().int(),
        pageSize: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
    }),
});
//# sourceMappingURL=fiscal-fx-source-review.js.map